import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Instagram,
  ArrowUp,
  Mail,
  CheckCircle2,
  Clock,
  Shield,
  Download,
  FileText,
  Droplet,
  Award,
  Leaf,
  CircleCheck,
  Facebook,
  Linkedin,
} from "lucide-react";
import { Button } from "./ui/Button";
import { PRODUCTS } from "../constants";

// Footer nav links – paths must match routes in App.tsx (Products listed separately)
const FOOTER_NAV_LINKS = [
  { label: "Home", path: "/" },
  { label: "About", path: "/about" },
  { label: "Sale Center Network", path: "/distributor" },
  { label: "Blog", path: "/blog" },
  { label: "Contacts", path: "/contact" },
] as const;

export const Footer: React.FC = () => {
  const navigate = useNavigate();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleFindDealer = () => {
    navigate("/distributor");
  };

  const handleContactClick = () => {
    navigate("/contact");
  };

  return (
    <>
      {/* Footer CTA Section */}
      <section className="relative h-fit w-full px-2 lg:px-12 py-8 lg:py-16">
        <img
          src="/assets/images/footer-cta-bg.webp"
          alt="Workshop"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black to-transparent"></div>

        <div className="relative h-full flex flex-col justify-center items-center lg:items-start text-center lg:text-left w-[90%] lg:w-[70%] mx-auto py-12 lg:py-0">
          <div className="max-w-4xl">
            <p className="text-3xl md:text-[40px] lg:text-heading-large text-white font-bold mb-8 font-open-sans-hebrew leading-tight">
              Share Your Project Needs For Expert{" "}
              <br className="hidden md:block" />
              Plywood Guidance
            </p>

            <div className="flex flex-col sm:flex-row flex-wrap gap-4 justify-center lg:justify-start">
              <Button
                variant="red"
                className="rounded-full px-8 py-3 lg:py-4 text-sm font-bold w-full sm:w-auto font-raleway"
                onClick={handleFindDealer}
              >
                Find Nearest Sale Center
              </Button>
              <Button
                variant="red"
                className="rounded-full px-8 py-3 lg:py-4 text-sm font-bold w-full sm:w-auto font-raleway"
                onClick={handleContactClick}
              >
                Let's Discuss
              </Button>
            </div>
          </div>

          <div className="flex lg:flex-row flex-col items-center lg:items-start gap-4 lg:gap-8 text-[10px] text-white font-bold uppercase tracking-widest border-t border-white/10 pt-8 mt-12 w-full lg:w-auto">
            <span className="flex items-center gap-2 bg-white/40 backdrop-blur-sm rounded-lg px-4 py-2 w-full sm:w-fit justify-center font-raleway">
              <CheckCircle2 size={14} className="text-white" /> Quick Response
            </span>
            <span className="flex items-center gap-2 bg-white/40 backdrop-blur-sm rounded-lg px-4 py-2 w-full sm:w-fit justify-center font-raleway">
              <Clock size={14} className="text-white" /> 24/7 Support Available
            </span>
            <span className="flex items-center gap-2 bg-white/40 backdrop-blur-sm rounded-lg px-4 py-2 w-full sm:w-fit justify-center font-raleway">
              <Shield size={14} className="text-white" /> Certified Service
              Centers
            </span>
          </div>
        </div>
      </section>

      <footer className="bg-black text-white pt-14 pb-8 px-2 lg:px-12">
        <div className="">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-16 mb-16">
            {/* Column 1 - Branding & Company Info */}
            <div className="flex flex-col items-center md:items-start">
              {/* Logo */}
              <div className="mb-6">
                <img
                  src="/assets/logos/logo.webp"
                  alt="Croma Ply"
                  className="w-[10rem]"
                />
              </div>

              {/* Company Description */}
              <div className="space-y-1 mb-8 text-center md:text-left font-raleway">
                <p className="text-gray-400 text-[10px] font-bold uppercase tracking-wider">
                  PREMIUM PLYWOOD FOR MODERN INTERIORS & ARCHITECTURE
                </p>
                <p className="text-gray-400 text-[10px] font-bold uppercase tracking-wider">
                  MANUFACTURED BY VIRGO PANEL PRODUCT
                </p>
              </div>

              {/* PDF Downloads */}
              <div className="flex flex-col items-center md:items-start">
                <h4 className="text-[10px] text-gray-500 font-bold uppercase tracking-widest font-raleway mb-4">
                  DOWNLOADS
                </h4>
                <div className="flex flex-row gap-2 w-full justify-center md:flex-col md:space-y-2 md:w-fit md:justify-start">
                  <a
                    href={`/view-pdf?file=${encodeURIComponent("/assets/pdfs/virgo-bronze.pdf")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 p-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded transition-all group flex-1 md:flex-none"
                  >
                    <div className="w-8 h-8 bg-white/10 rounded flex items-center justify-center border border-white/20 group-hover:bg-white/20 transition-colors flex-shrink-0">
                      <FileText size={14} className="text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-[10px] font-bold text-white mb-0.5">
                        ZED Bronze Certificate
                      </h4>
                      <p className="text-[9px] text-gray-400 uppercase tracking-wide">
                        PDF
                      </p>
                    </div>
                    <Download
                      size={12}
                      className="text-gray-400 group-hover:text-white transition-colors flex-shrink-0"
                    />
                  </a>

                  <a
                    href={`/view-pdf?file=${encodeURIComponent("/assets/pdfs/croma-brochure.pdf")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 p-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded transition-all group flex-1 md:flex-none"
                  >
                    <div className="w-8 h-8 bg-white/10 rounded flex items-center justify-center border border-white/20 group-hover:bg-white/20 transition-colors flex-shrink-0">
                      <FileText size={14} className="text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-[10px] font-bold text-white mb-0.5">
                        Product Brochure
                      </h4>
                      <p className="text-[9px] text-gray-400 uppercase tracking-wide">
                        PDF
                      </p>
                    </div>
                    <Download
                      size={12}
                      className="text-gray-400 group-hover:text-white transition-colors flex-shrink-0"
                    />
                  </a>
                </div>
              </div>
            </div>

            {/* Column 2 - Navigation & Address */}
            <div className="flex flex-col items-center md:items-start gap-8 ">
              {/* Office Address */}
              <div>
                <h4 className="text-[10px] text-gray-500 font-bold uppercase tracking-widest font-raleway mb-2 text-center md:text-left">
                  OFFICE ADDRESS:
                </h4>
                <address className="not-italic text-sm text-white font-normal font-raleway leading-relaxed tracking-wide text-center md:text-left">
                  Virgo Panel Product Pvt. Ltd.
                  <br />
                  Village Daulowal, Dasua Road
                  <br />
                  Hoshiarpur, Punjab, India
                </address>
              </div>

              {/* Navigation */}
              <div>
                <nav className="flex flex-col items-center md:items-start gap-2">
                  {FOOTER_NAV_LINKS.map((link) => (
                    <Link
                      to={link.path}
                      key={link.label}
                      className="text-xs text-gray-400 hover:text-white uppercase tracking-wider transition-colors font-raleway"
                    >
                      {link.label}
                    </Link>
                  ))}
                </nav>
              </div>
            </div>

            {/* Column 3 - Products */}
            <div className="flex flex-col items-center md:items-start">
              <Link
                to="/products"
                className="text-[10px] text-gray-500 font-raleway font-bold uppercase tracking-widest mb-3 block hover:text-white transition-colors text-center md:text-left"
              >
                PRODUCTS
              </Link>
              <nav className="flex flex-col items-center md:items-start gap-2">
                {PRODUCTS.map((product) => (
                  <Link
                    key={product.id}
                    to={`/products/${product.id}`}
                    className="text-xs text-gray-400 hover:text-white transition-colors font-bold line-clamp-1 font-raleway"
                  >
                    {product.name}
                  </Link>
                ))}
              </nav>
            </div>

            {/* Column 4 - Contact Info */}
            <div className="flex flex-col items-center md:items-start">
              <h4 className="text-[10px] text-gray-500 font-bold font-raleway uppercase tracking-widest mb-3 text-center md:text-left">
                WE'RE HERE TO ANSWER YOUR QUESTIONS
              </h4>
              <div className="space-y-2 mb-6">
                <div className="flex items-center justify-center md:justify-start gap-1 flex-wrap">
                  <a
                    href="tel:+917087599909"
                    className="text-lg text-white font-light font-raleway hover:text-gray-300 transition-colors tracking-tight"
                  >
                    +91 73470 12168
                  </a>
                  <span className="text-gray-500 mr-2">,</span>
                  <a
                    href="tel:+917347012168"
                    className="text-lg text-white font-light font-raleway hover:text-gray-300 transition-colors tracking-tight"
                  >
                    +91 89680 99909
                  </a>
                </div>
              </div>

              <a
                href="mailto:plycroma@gmail.com"
                className="flex items-center justify-center md:justify-start font-raleway gap-3 text-xs text-white hover:text-gray-300 uppercase tracking-widest font-medium mb-8"
              >
                <Mail size={16} className="text-gray-500" />
                plycroma@gmail.com
              </a>

              <div className="flex flex-col items-center md:items-start">
                <h4 className="text-[10px] text-gray-600 font-bold uppercase font-raleway tracking-widest mb-6 text-center md:text-left">
                  QUICK CONTACT WITH OUR LOGISTICS TEAM
                </h4>
                <div className="flex justify-center md:justify-start gap-4">
                  <a
                    href="https://www.facebook.com/cromaply"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 border border-white/10 rounded-lg flex items-center justify-center hover:bg-white hover:text-black transition-all text-white bg-black group"
                  >
                    <Facebook
                      size={18}
                      className="group-hover:scale-110 transition-transform"
                    />
                  </a>
                  <a
                    href="https://x.com/CromaPly"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 border border-white/10 rounded-lg flex items-center justify-center hover:bg-white hover:text-black transition-all text-white bg-black group"
                  >
                    <svg
                      className="fill-current transition-colors"
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="currentColor"
                    >
                      <path d="M12.6.75h2.454l-5.36 6.142L16 15.25h-4.937l-3.867-5.07-4.425 5.07H.316l5.733-6.57L0 .75h5.063l3.495 4.633L12.601.75Zm-.86 13.028h1.36L4.323 2.145H2.865z" />
                    </svg>
                  </a>
                  <a
                    href="https://www.instagram.com/cromaply?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw=="
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 border border-white/10 rounded-lg flex items-center justify-center hover:bg-white hover:text-black transition-all text-white bg-black group"
                  >
                    <Instagram
                      size={18}
                      className="group-hover:scale-110 transition-transform"
                    />
                  </a>
                  <a
                    href="https://www.linkedin.com/in/croma-ply/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 border border-white/10 rounded-lg flex items-center justify-center hover:bg-white hover:text-black transition-all text-white bg-black group"
                  >
                    <Linkedin
                      size={18}
                      className="group-hover:scale-110 transition-transform"
                    />
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-white/10 py-6 md:py-3">
            <div className="flex flex-wrap items-center justify-center lg:justify-between font-raleway gap-6 md:gap-8 lg:gap-12">
              <div className="flex items-center gap-2">
                <Shield size={16} className="text-white" />
                <p className="text-xs text-gray-200 uppercase tracking-wider font-medium">
                  IS 710:2010
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Droplet size={16} className="text-white" />
                <p className="text-xs text-gray-200 uppercase tracking-wider font-medium">
                  IS 5509:2000
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Award size={16} className="text-white" />
                <p className="text-xs text-gray-200 uppercase tracking-wider font-medium">
                  ISO 9001:2015
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Leaf size={16} className="text-white" />
                <p className="text-xs text-gray-200 uppercase tracking-wider font-medium">
                  ECO MARK
                </p>
              </div>
              <div className="flex items-center gap-2">
                <CircleCheck size={16} className="text-white" />
                <p className="text-xs text-gray-200 uppercase tracking-wider font-medium">
                  ZED CERTIFIED
                </p>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="pt-5 flex flex-col md:flex-row justify-between items-center md:items-start text-[10px] text-gray-500 uppercase tracking-wider font-medium gap-10 border-t border-white/10">
            <div className="flex flex-wrap justify-center md:justify-start gap-x-12 gap-y-3 max-w-4xl w-full md:w-auto">
              <div className="flex flex-col gap-3">
                <Link
                  to="/privacy"
                  className="hover:text-white transition-colors text-left uppercase font-raleway"
                >
                  PRIVACY POLICY
                </Link>
              </div>
              <div className="flex flex-col gap-3">
                <Link
                  to="/privacy"
                  className="hover:text-white transition-colors text-left uppercase font-raleway"
                >
                  TERMS & CONDITIONS
                </Link>
              </div>
            </div>

            <div className="flex flex-col md:flex-row items-center md:items-end font-raleway gap-6 md:gap-12 w-full md:w-auto justify-between flex-1 md:flex-none">
              <span className="text-gray-600 text-center md:text-left">
                © 2026 POWERED BY{" "}
                <a
                  href="https://techinject.in"
                  target="_blank"
                  rel="noopener noreferrer"
                  className=" hover:text-orange-300 transition-colors"
                >
                  TECH INJECT GLOBAL SOLUTIONS.
                </a>
              </span>
              <button
                onClick={scrollToTop}
                className="flex items-center gap-2 hover:text-white transition-colors group text-white"
              >
                BACK TOP{" "}
                <ArrowUp
                  size={12}
                  className="group-hover:-translate-y-1 transition-transform"
                />
              </button>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};
